/**
 * Settings Index Redirect Component
 *
 * Smart redirect for /settings based on user permissions.
 * Redirects to the first available settings page the user has access to.
 *
 * @module components/layout/SettingsIndexRedirect
 */

import { Navigate } from 'react-router-dom';
import { usePermissions } from '@/hooks';
import { Spinner } from '@/components/ui';

// =============================================================================
// Component
// =============================================================================

/**
 * Redirects users to the appropriate settings page based on their permissions.
 *
 * Redirect priority:
 * 1. /settings/organization (if canUpdateOrgSettings)
 * 2. /settings/team (if canManageTeam)
 * 3. /settings/billing (if canAccessBilling)
 * 4. /profile (fallback for users without settings access)
 */
export function SettingsIndexRedirect() {
  const { canUpdateOrgSettings, canManageTeam, canAccessBilling, loading } = usePermissions();

  // Show loading state while permissions are being determined
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Spinner size="lg" />
      </div>
    );
  }

  // Redirect based on permissions (in priority order)
  if (canUpdateOrgSettings) {
    return <Navigate to="/settings/organization" replace />;
  }

  if (canManageTeam) {
    return <Navigate to="/settings/team" replace />;
  }

  if (canAccessBilling) {
    return <Navigate to="/settings/billing" replace />;
  }

  // No settings access - redirect to profile
  return <Navigate to="/profile" replace />;
}
